const EASING = {
  cubic: "cubic-bezier(0.17, 0.4, 0.48, 0.89)",
};

const ANIMATIONS = {
  default: {
    keyframes: [
      { transform: "translateY(20px)", opacity: 0 },
      { transform: "translateY(0)", opacity: 1 },
    ],
    options: {
      delay: 250,
      duration: 750,
      easing: EASING.cubic,
      fill: "backwards",
    },
    stagger: 75,
  },
  "slide-up-large": {
    keyframes: [
      { transform: "translateY(250px)", opacity: 0 },
      { transform: "translateY(0)", opacity: 1 },
    ],
    options: {
      delay: 150,
      duration: 1200,
      // easing: "ease-out",
      easing: EASING.cubic,
      fill: "backwards",
    },
    stagger: 75,
  },
  "slide-up-full": {
    keyframes: [
      { transform: "translateY(100%)", opacity: 0 },
      { transform: "translateY(0)", opacity: 1 },
    ],
    options: {
      delay: 150,
      duration: 1200,
      easing: EASING.cubic,
      fill: "backwards",
    },
    stagger: 75,
  },
  "slide-down-full": {
    keyframes: [
      { transform: "translateY(0)" },
      { transform: "translateY(100%)" },
    ],
    options: {
      delay: 150,
      duration: 1200,
      easing: EASING.cubic,
      fill: "both",
    },
    stagger: 75,
  },
  "slide-up-small": {
    keyframes: [
      { transform: "translateY(100px)", opacity: 0 },
      { transform: "translateY(0)", opacity: 1 },
    ],
    options: {
      delay: 150,
      duration: 600,
      easing: EASING.cubic,
      fill: "backwards",
    },
    stagger: 75,
  },
  "fade-in": {
    keyframes: [{ opacity: 0 }, { opacity: 1 }],
    options: {
      delay: 150,
      duration: 500,
      easing: EASING.cubic,
      fill: "backwards",
    },
    stagger: 50,
  },
  "stretch-x": {
    keyframes: [{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }],
    options: {
      delay: 150,
      duration: 500,
      easing: EASING.cubic,
      fill: "backwards",
    },
    stagger: 50,
  },
  display: {
    keyframes: [{ display: "none" }, { display: "block" }],
    options: {
      delay: 0,
      duration: 1,
      easing: "linear",
      fill: "backwards",
    },
    stagger: 50,
  },
  none: {
    keyframes: [{}, {}],
    options: {
      duration: 750,
      easing: EASING.cubic,
      fill: "backwards",
    },
    stagger: 50,
  },
};

const normalizeSubTarget = (subTarget, parentStagger, animation) => ({
  selector: typeof subTarget == "string" ? subTarget : subTarget.selector,
  stagger: subTarget?.stagger
    ? subTarget.stagger
    : parentStagger || ANIMATIONS[animation].stagger,
  animation:
    subTarget?.animation && subTarget?.animation in ANIMATIONS
      ? subTarget.animation
      : animation,
  options: subTarget?.options,
});

const normalizeTargets = (data) => {
  const targets = data.targets.map((target) => {
    const animation =
      target.animation && target.animation in ANIMATIONS
        ? target.animation
        : data.animation && data.animation in ANIMATIONS
          ? data.animation
          : "default";

    let subTargets = null;

    if (target.subTarget) {
      const raw = Array.isArray(target.subTarget)
        ? target.subTarget
        : [target.subTarget];
      subTargets = raw.map((st) =>
        normalizeSubTarget(st, target.stagger, animation),
      );
    }

    const normalizedTarget = {
      selector: typeof target == "string" ? target : target.selector,
      subTargets,
      stagger: target?.stagger || ANIMATIONS[animation].stagger,
      animation,
      options: target?.options,
    };

    return normalizedTarget;
  });

  return targets;
};

class AnimateInView extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion)").matches) {
      this.setAttribute("loaded", "");
      return;
    }

    try {
      const data = JSON.parse(this.querySelector("script").innerHTML);
      this.targets = normalizeTargets(data);
    } catch (error) {
      console.warn("Error parsing animation data", error);
      return;
    }

    this.animations = new WeakMap();
    this.observers = [];

    this.targets.forEach((target) => {
      this.targetEls = this.section.querySelectorAll(target.selector);

      this.targetEls.forEach((targetEl) => {
        this.initAnimation({ target, targetEl });

        if (!target.subTargets) return;

        target.subTargets.forEach((subTarget) => {
          const subTargetEls = targetEl.querySelectorAll(subTarget.selector);
          subTargetEls.forEach((subTargetEl) =>
            this.initAnimation({ target: subTarget, targetEl: subTargetEl }),
          );
        });
      });

      const observer = new IntersectionObserver(
        (entries) => {
          entries
            .filter((entry) => entry.isIntersecting)
            .forEach((entry, i) => {
              observer.unobserve(entry.target);

              const { stagger } = this.animations.get(entry.target);

              const delay = i * stagger;
              this.playAnimation(entry.target, delay);

              if (!target.subTargets) return;

              const subTargetStagger = target.subTargets[0].stagger;
              let subTargetIndex = 0;

              target.subTargets.forEach((subTarget) => {
                const subTargetEls = entry.target.querySelectorAll(
                  subTarget.selector,
                );
                subTargetEls.forEach((subTargetEl) => {
                  const subTargetDelay =
                    subTargetIndex * subTargetStagger + delay;
                  subTargetIndex++;
                  this.playAnimation(subTargetEl, subTargetDelay);
                });
              });
            });
        },
        {
          rootMargin: "0% 0% 0% 0%",
        },
      );

      this.targetEls.forEach((targetEl) => observer.observe(targetEl));

      this.observers.push(observer);

      this.setAttribute("loaded", "");
    });
  }

  playAnimation(el, delay) {
    const { animation } = this.animations.get(el);

    if (!animation) {
      console.warn("no animation found for", el);
      return;
    }

    if (delay) {
      window.setTimeout(() => animation.play(), delay);
    } else {
      animation.play();
    }
  }

  initAnimation({ target, targetEl }) {
    const { keyframes, options } = ANIMATIONS[target.animation];

    const targetOptions = target.options || {};
    const keyframeEffect = new KeyframeEffect(targetEl, keyframes, {
      ...options,
      ...targetOptions,
    });
    const animation = new Animation(keyframeEffect);
    animation.pause();

    this.animations.set(targetEl, { animation, stagger: target.stagger });
  }

  get section() {
    return this.parentElement;
  }

  disconnectedCallback() {
    this.observers.forEach((observer) => observer.disconnect());
  }
}

if (!customElements.get("animate-in-view")) {
  customElements.define("animate-in-view", AnimateInView);
}
